// Peak level meters (dBFS) for every audio channel.
// The AudioWorklet reports the sample peak of each channel about 30 times per second;
// AudioMeters keeps the falling bars and peak holds and draws them on a canvas.

const PROCESSOR_NAME = "ks-peak-meter";

const PROCESSOR_CODE = `
class PeakMeterProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.block = Math.round(sampleRate / 30);
    this.count = 0;
    this.peaks = [];
  }
  process(inputs) {
    var input = inputs[0] || [];
    var n = input.length;
    if (this.peaks.length !== n) this.peaks = new Array(n).fill(0);
    var len = n ? input[0].length : 128;
    for (var c = 0; c < n; c++) {
      var x = input[c];
      var p = this.peaks[c];
      for (var i = 0; i < x.length; i++) {
        var v = x[i] < 0 ? -x[i] : x[i];
        if (v > p) p = v;
      }
      this.peaks[c] = p;
    }
    this.count += len;
    if (this.count >= this.block) {
      // No channels while the element has no audio: reported as an empty list
      this.port.postMessage(this.peaks);
      this.peaks = new Array(n).fill(0);
      this.count = 0;
    }
    return true;
  }
}

registerProcessor("${PROCESSOR_NAME}", PeakMeterProcessor);
`;

const loaded = new WeakMap();

// Creates the meter node for the context; the module is loaded once per context
export function createPeakMeter(ctx) {
	var promise = loaded.get(ctx);
	if (!promise) {
		var url = URL.createObjectURL(new Blob([PROCESSOR_CODE], {type: "application/javascript"}));
		promise = ctx.audioWorklet.addModule(url).finally(() => URL.revokeObjectURL(url));
		loaded.set(ctx, promise);
	}
	return promise.then(() => new AudioWorkletNode(ctx, PROCESSOR_NAME, {numberOfOutputs: 1, outputChannelCount: [1]}));
}

const MIN_DB = -60;
const FALL_DB_PER_S = 24;
const HOLD_MS = 1500;
const SCALE = [0, -6, -12, -18, -24, -30, -40, -50, -60];
// Color zones from the bottom up: dark green, green, bright green, yellow and red (> -1 dB)
const ZONES = [
	{to: -36, lit: "#2f7d2f", unlit: "rgba(47, 125, 47, 0.4)"},
	{to: -18, lit: "#3fa63f", unlit: "rgba(63, 166, 63, 0.4)"},
	{to: -6, lit: "#72e03c", unlit: "rgba(114, 224, 60, 0.36)"},
	{to: -1, lit: "#f2e33a", unlit: "rgba(242, 227, 58, 0.36)"},
	{to: 0, lit: "#ec3b2b", unlit: "rgba(236, 59, 43, 0.42)"}
];

function zone_color(db) {
	var zone = ZONES.find(z => db <= z.to) || ZONES[ZONES.length - 1];
	return zone.lit;
}

// IEC 60268-18 meter deflection, 0 at -60 dB and 1 at 0 dB
function deflection(db) {
	var d;
	if (db < -70) d = 0;
	else if (db < -60) d = (db + 70) * 0.25;
	else if (db < -50) d = (db + 60) * 0.5 + 2.5;
	else if (db < -40) d = (db + 50) * 0.75 + 7.5;
	else if (db < -30) d = (db + 40) * 1.5 + 15;
	else if (db < -20) d = (db + 30) * 2 + 30;
	else if (db < 0) d = (db + 20) * 2.5 + 50;
	else d = 100;
	return Math.max(0, (d - 2.5) / 97.5);
}

function channel_labels(n) {
	if (n === 1) return ["M"];
	if (n === 2) return ["L", "R"];
	if (n === 6) return ["L", "R", "C", "LFE", "Ls", "Rs"];
	return Array.from({length: n}, (v, i) => String(i + 1));
}

export class AudioMeters {
	constructor (canvas) {
		this.canvas = canvas;
		this.levels = [];
		this.holds = [];
		this.last = 0;
		this.width = 0;
	}
	// peaks: linear sample peaks per channel; an empty list keeps the bars and lets them fall
	add (peaks, now) {
		var dt = this.last ? Math.min(0.5, (now - this.last) / 1000) : 0;
		this.last = now;
		// A paused element gives silence on a single channel; only real audio changes the channel count
		if (peaks.length !== this.levels.length && peaks.length && (!this.levels.length || peaks.some(p => p > 0))) {
			this.levels = new Array(peaks.length).fill(-Infinity);
			this.holds = peaks.map(() => ({db: -Infinity, time: 0}));
		}
		var same = peaks.length === this.levels.length;
		for (var c = 0; c < this.levels.length; c++) {
			var db = same && peaks[c] > 0 ? 20 * Math.log10(peaks[c]) : -Infinity;
			var fallen = this.levels[c] - FALL_DB_PER_S * dt;
			this.levels[c] = Math.max(db, fallen);
			var hold = this.holds[c];
			if (db >= hold.db || now - hold.time > HOLD_MS) {
				hold.db = db;
				hold.time = now;
			}
		}
	}
	// Draws the meters and returns the CSS width of the canvas, which depends on the
	// channel count and the canvas height only
	draw () {
		var canvas = this.canvas;
		var n = this.levels.length;
		var h = canvas.clientHeight;
		if (!n || !h) return 0;
		var font = Math.round(Math.min(10, Math.max(7, h * 0.026)));
		var show_scale = h >= 110;
		var bar = Math.round(Math.min(12, Math.max(4, h * 0.028)));
		var gap = Math.max(2, Math.round(bar * 0.5));
		var pad = Math.max(3, Math.round(font * 0.4));
		var pad_x = 1;
		var scale_w = show_scale ? Math.ceil(font * 1.9) + 4 : 0;
		var label_h = show_scale ? font + 3 : 0;
		var width = pad_x + scale_w + n * bar + (n - 1) * gap + pad_x;
		if (width !== this.width) {
			this.width = width;
			canvas.style.width = width + "px";
		}
		var dpr = window.devicePixelRatio || 1;
		var pw = Math.round(width * dpr);
		var ph = Math.round(h * dpr);
		if (canvas.width !== pw || canvas.height !== ph) {
			canvas.width = pw;
			canvas.height = ph;
		}
		var g = canvas.getContext("2d");
		g.setTransform(dpr, 0, 0, dpr, 0, 0);
		g.clearRect(0, 0, width, h);

		var top = pad + label_h + (show_scale ? Math.ceil(font / 2) : 0);
		var bottom = h - pad - (show_scale ? Math.ceil(font / 2) : 0);
		var span = Math.max(1, bottom - top);
		var y_of = (db) => bottom - deflection(db) * span;
		var x0 = pad_x + scale_w;

		g.font = font + "px Roboto, sans-serif";
		g.textBaseline = "middle";
		if (show_scale) {
			// No background, so the texts get a dark shadow to stay readable on a bright picture
			g.save();
			g.shadowColor = "rgba(0, 0, 0, 0.9)";
			g.shadowBlur = 2;
			g.fillStyle = "rgba(255, 255, 255, 0.95)";
			g.textAlign = "right";
			SCALE.forEach((db) => {
				var y = Math.round(y_of(db)) + 0.5;
				g.fillText(String(db), x0 - 5, y);
				g.fillRect(x0 - 3, y - 0.5, 2, 1);
			});
			g.textAlign = "center";
			channel_labels(n).forEach((label, c) => {
				var x = x0 + c * (bar + gap) + bar / 2;
				g.font = (label.length > 2 ? Math.max(6, font - 2) : font) + "px Roboto, sans-serif";
				g.fillText(label, x, pad + label_h / 2);
			});
			g.restore();
		}

		for (var c = 0; c < n; c++) {
			var x = x0 + c * (bar + gap);
			var level = this.levels[c];
			var lit_y = level > MIN_DB ? y_of(level) : bottom;
			// Every zone is drawn dim above the level and in full color below it
			var from_y = bottom;
			ZONES.forEach((zone) => {
				var to_y = y_of(zone.to);
				var split = Math.min(from_y, Math.max(to_y, lit_y));
				if (split > to_y) {
					g.fillStyle = zone.unlit;
					g.fillRect(x, to_y, bar, split - to_y);
				}
				if (from_y > split) {
					g.fillStyle = zone.lit;
					g.fillRect(x, split, bar, from_y - split);
				}
				from_y = to_y;
			});
			var hold = this.holds[c].db;
			if (hold > MIN_DB) {
				g.fillStyle = zone_color(hold);
				g.fillRect(x, Math.round(y_of(hold)) - 1, bar, 2);
			}
		}
		// Thin dividers over the bars like on the reference meter
		if (show_scale) {
			g.fillStyle = "rgba(0, 0, 0, 0.35)";
			SCALE.forEach((db) => {
				var y = Math.round(y_of(db));
				g.fillRect(x0, y, n * bar + (n - 1) * gap, 1);
			});
		}
		return width;
	}
}
